<template>
  <div class="preview">
    <div class="preview__box" :class="previewClass" :style="{ backgroundColor: selection }">
      <img
        v-if="imagePreview"
        :src="imagePreview"
        alt="preview"
        class="preview__image"
        @load="handleImageLoad"
      />
      <Spinner v-if="letterPreviewLoading" class="preview__spinner" />
    </div>
    <p class="preview__label">Change Background Color</p>
    <ColorPicker :value="selection" :colors="colors" @change="handleSelect" />
    <p class="preview__preview-notice">Preview Only: Background Color Won't Be Printed</p>
  </div>
</template>

<script setup>
import { ref, computed, watchEffect } from "vue";
import Spinner from "components/jiffy/vue-cart/Spinner.vue";
import ColorPicker from "components/jiffy/vue-product-details-dtf/ColorPicker.vue";
import { useDtfStore } from "../store";

const colors = [
  "#000000",
  "#FFFFF0",
  "#4169E1",
  "#DC143C",
  "#228B22",
  "#671EA1",
  "#708090",
  "#FFD700",
  "#CC5500",
  "#191970",
  "#800020"
];
const defaultStyle = "straight";
const touched = ref(false);
const store = useDtfStore();
const selection = ref(colors[1]);
const letterNumberArtwork = computed(() => store.letterNumberArtwork);
const currentColor = computed(() => letterNumberArtwork.value.color || "#000000");
const previewClass = ref(`preview__box--${letterNumberArtwork.value.style || defaultStyle}`);
const imagePreview = computed(() => {
  const { style, url } = letterNumberArtwork.value;
  if (style) {
    return url;
  }
  return `https://jiffy.imgix.net/gen/letters-pdp/${defaultStyle}-2.png`;
});
const letterPreviewLoading = computed(() => store.letterPreviewLoading);
const dtfMaxHeight = computed(() => store.dtfMaxHeight);
const imageScale = computed(() => {
  const { sizes, style } = letterNumberArtwork.value;

  // If the current textbox index dimensions is not yet ready. Default to dimensions with
  // undefined key (default dimensions).
  // This avoids jumpiness on the UI.
  const activeSize = sizes[letterNumberArtwork.value.textIndex] || sizes[undefined];

  if (style === "vertical" || !activeSize) return "scale(1)";

  let scaleValue = activeSize.height / dtfMaxHeight.value;

  // Ensures that image is not distorted when scale value is greater than 1
  if (scaleValue > 1) scaleValue = 1;

  return `scale(1, ${scaleValue})`;
});

watchEffect(() => {
  if (!touched.value) {
    selection.value = currentColor.value === "#FFFFFF" ? "#000000" : "#FFFFF0";
  }
});

const handleSelect = color => {
  touched.value = true;
  selection.value = color;
};

const handleImageLoad = () => {
  store.setLetterPreviewLoading(false);
  previewClass.value = `preview__box--${letterNumberArtwork.value.style || defaultStyle}`;
};
</script>

<style lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.preview {
  $this: &;

  &__content {
    flex: 1;
    margin-bottom: 16px;

    @include with-large-desktop {
      flex: 1 1 50%;
    }
  }

  &__box {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    min-height: 50px;

    &--straight {
      #{$this}__image {
      }
    }

    &--vertical {
      #{$this}__image {
        object-fit: contain;
        width: 26px;
      }
    }
  }

  &__label {
    margin: 20px 0;
    color: $text-black;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.1;
  }

  &__preview-notice {
    margin: 20px 0;
    color: $text-black;
    font-style: italic;
  }

  &__image {
    width: 100%;
    height: 100%;
    transform: v-bind(imageScale);
  }

  .color-picker__modal {
    @include with-tablet {
      top: auto;
      left: auto;
      margin-left: unset;
    }
  }

  &__spinner {
    position: absolute;
    width: 30px;
  }
}
</style>
